import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Globe, Lock, User, Users } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../../lib/utils';
import { specraBadgeVariants } from '../ensigns/badge';
import { specraFocusRing } from '../ensigns/focus-ring';
import { SpecraIcon } from '../ensigns/icon';
import { SpecraTooltip } from './tooltip';
const LEVEL = {
    personal: {
        icon: Lock,
        variant: 'neutral'
    },
    team: {
        icon: Users,
        variant: 'info'
    },
    people: {
        icon: User,
        variant: 'info'
    },
    everyone: {
        icon: Globe,
        variant: 'warning'
    }
};
const SIZE = {
    xs: 'h-[var(--control-height-xs)] gap-[var(--space-1)] px-[var(--space-1-5)] py-0 text-[length:var(--fs-ui-meta)]',
    sm: 'h-[var(--control-height-sm)] gap-[var(--space-1-5)] px-[var(--space-2)] py-0 text-[length:var(--fs-ui-caption)]'
};
/** "Who can see this" chip: the audience level as icon + tone + a sentence. */ export const SpecraAudienceChip = /*#__PURE__*/ React.forwardRef(({ className, detail, disabled, level, onActivate, onClick, readOnly = false, sharedBy, size = 'xs', summary, ...props }, ref)=>{
    const { icon: Icon, variant } = LEVEL[level];
    const classes = cn(specraBadgeVariants({
        variant
    }), SIZE[size], 'max-w-full font-medium', className);
    const icon = /*#__PURE__*/ _jsx(SpecraIcon, {
        size: "sm",
        children: /*#__PURE__*/ _jsx(Icon, {})
    });
    if (readOnly) {
        // Forward span-safe props (data-*, aria-*, id, style…); drop the
        // button-only ones and tabIndex, since a read-only chip never takes focus.
        const { autoFocus: _autoFocus, form: _form, formAction: _formAction, formEncType: _formEncType, formMethod: _formMethod, formNoValidate: _formNoValidate, formTarget: _formTarget, name: _name, tabIndex: _tabIndex, type: _type, value: _value, ...spanProps } = props;
        const chip = /*#__PURE__*/ _jsxs("span", {
            ...spanProps,
            className: classes,
            "data-audience-level": level,
            "data-read-only": "",
            ref: ref,
            children: [
                icon,
                /*#__PURE__*/ _jsx("span", {
                    className: "sr-only",
                    children: "Who can see this: "
                }),
                /*#__PURE__*/ _jsxs("span", {
                    className: "truncate",
                    children: [
                        sharedBy ? `Shared by ${sharedBy} · ` : null,
                        summary
                    ]
                }),
                detail ? /*#__PURE__*/ _jsxs("span", {
                    className: "sr-only",
                    children: [
                        ", ",
                        detail
                    ]
                }) : null
            ]
        });
        // Hover-only tooltip: a read-only chip never takes focus, and its detail
        // is already in the accessible text above.
        return detail ? /*#__PURE__*/ _jsx(SpecraTooltip, {
            content: detail,
            children: chip
        }) : chip;
    }
    const button = /*#__PURE__*/ _jsxs("button", {
        ...props,
        "aria-label": `Who can see this: ${summary}, change`,
        className: cn(classes, specraFocusRing, 'cursor-pointer transition-colors hover:border-foreground/30 disabled:pointer-events-none disabled:opacity-50'),
        "data-audience-level": level,
        disabled: disabled,
        onClick: (event)=>{
            onClick?.(event);
            if (!event.defaultPrevented) onActivate?.();
        },
        ref: ref,
        type: "button",
        children: [
            icon,
            /*#__PURE__*/ _jsx("span", {
                className: "truncate",
                children: summary
            })
        ]
    });
    return detail ? /*#__PURE__*/ _jsx(SpecraTooltip, {
        content: detail,
        children: button
    }) : button;
});
SpecraAudienceChip.displayName = 'SpecraAudienceChip';
